import React, { useEffect, useMemo, useState } from "react";
import {
  DangerButton,
  PrimaryButton,
  SecondaryButton,
} from "../../components/Buttons.jsx";
import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";

import { useDispatch, useSelector } from "react-redux";
import {
  FaCircleCheck,
  FaTriangleExclamation,
  FaRegFilePdf,
} from "react-icons/fa6";
import {
  getInvoices,
  getPendingOrderCharges,
  getSubscription,
  savePaymentCard,
  saveSellerPaymentCard,
  getStore,
} from "../../api/settings.js";
import { Elements } from "@stripe/react-stripe-js";

import { addToast, getMerchants } from "../../store/app/action.js";
import { formatValue } from "../../utils/Utils.js";
import moment from "moment";
import PastOrderModal from "../pastOrders/PastOrderModal.jsx";
import { FaRegEye } from "react-icons/fa6";
import PaginationNumeric from "../../components/PaginationNumeric.jsx";
import InvoiceModal from "./InvoiceModal.jsx";
import ModalBasic from "../../components/ModalBasic.jsx";
import { chargeAllFeesNow } from "../../api/admin.js";
import CanView from "../../components/CanView.jsx";
import config from '@/config';
import PlanModal from "../planModal/PlanModal.jsx";
import DateSelect from "../../components/DateSelect.jsx";
import { loadStripe } from "@stripe/stripe-js";
export const GetCardIcon = ({ brand }) => {
  const brandMap = {
    "amex": "amex",
    "diners club": "diners",
    discover: "discover",
    jcb: "jcb",
    mastercard: "mastercard",
    unionpay: "generic",
    visa: "visa",
    unknown: "generic",
  };

  return (
    "https://cdn.saucey.com/direct/web/payment-logos/" +
    (brandMap[brand] ?? "generic") +
    ".svg"
  );
};

export const GetCardBrandFriendly = ({ brand }) => {
  const brandMap = {
    "amex": "American Express",
    "diners club": "Diners Club",
    discover: "Discover",
    jcb: "JCB",
    mastercard: "Mastercard",
    unionpay: "Generic",
    visa: "Visa",
    unknown: "Generic",
  };

  return brandMap[brand] ?? "Generic";
};
const stripePromise = loadStripe(config.stripe.pk);
const stripeSubscriptionPromise = loadStripe(config.stripe.subscriptionPK);
const SubscriptionElement = ({ children }) => {
  return <Elements stripe={stripeSubscriptionPromise}>{children}</Elements>;
};
const BillingElement = ({ children }) => {
  return <Elements stripe={stripePromise}>{children}</Elements>;
};

const CardInput = ({
  merchant,
  expiresIn,
  level = "merchant",
  addCard,
  setAddCard,
  otherAddCard,
  setOtherAddCard,
  stripeCardId,
  stripeCardBrand,
  stripeCardExpMonth,
  stripeCardExpYear,
  stripeCardLast4,
  stripeCardChargeFailed,
  setForceGetInvoice,
  setMerchant,
}) => {
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);
  const stripe = useStripe();
  const elements = useElements();
  const dispatch = useDispatch();

  useEffect(() => {
    if (addCard) {
      setAgreed(false);
    }
  }, [addCard]);

  const saveCard = async (e) => {
    e.preventDefault();
    setSaving(true);

    if (!stripe || !elements) {
      return;
    }

    const cardElement = elements.getElement(CardElement);

    // Create a payment method
    const { error, paymentMethod } = await stripe.createPaymentMethod({
      type: "card",
      card: cardElement,
      allow_redisplay: "always",
    });
    if (error) {
      setSaving(false);
      return;
    }

    try {
      let method =
        level === "merchant" ? savePaymentCard : saveSellerPaymentCard;
      const ret = await method({
        id: paymentMethod.id,
        card: paymentMethod.card,
      });
      if (level === "merchant") {
        setMerchant(ret.org);
      }
      setAddCard(false);
      setOtherAddCard(false);
      setForceGetInvoice(new Date());
      dispatch(
        addToast({
          type: "success",
          message: "Card saved successfully",
          timer: 5,
        })
      );
      dispatch(getMerchants());
    } catch (e) {
      let message = "please try again";
      try {
        const serverMessage = e.response?.data?.error
        if (serverMessage) {
          message = serverMessage;
        }
      } catch (e) {
      }

      dispatch(
        addToast({
          type: "error",
          message: "Error saving card, " + message,
          timer: 5,
        })
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={
        "flex flex-col gap-6 w-[410px] border-slate-1200 h-[196px] border rounded-lg px-4 justify-center"
      }
    >
      {!stripeCardId && !addCard && (
        <span className={"font-bold flex items-center gap-2"}>
          <FaTriangleExclamation className="text-red-500" />
          No payment method on file
        </span>
      )}
      {stripeCardId && !addCard && (
        <>
          <div className="flex items-center gap-4 text-sm">
            <img
              src={GetCardIcon({ brand: stripeCardBrand })}
              alt={stripeCardBrand}
              className="h-6"
            />
            <div>
              <div className="font-bold flex gap-3">
                <span>
                  {GetCardBrandFriendly({
                    brand: stripeCardBrand,
                  })}
                </span>
                <span>•••• {stripeCardLast4}</span>
              </div>
              <div className="text-slate-400">
                {/* pad month with zero if needed */}
                {stripeCardExpMonth.padStart(2, "0")}/{stripeCardExpYear}
              </div>
            </div>
          </div>
        </>
      )}
      {!addCard && (expiresIn < 31 || Boolean(stripeCardChargeFailed)) && (
        <div>
          {!addCard && expiresIn < 31 && expiresIn > 0 && (
            <div className="text-sm font-bold text-[orange] flex gap-2 items-center">
              <FaTriangleExclamation />
              Your card expires in {Math.ceil(expiresIn)} days, please update
              soon.
            </div>
          )}
          {!addCard && expiresIn < 0 && (
            <div className="text-sm  font-bold text-red-500 flex gap-2 items-center">
              <FaTriangleExclamation />
              Your card has expired, please update now.
            </div>
          )}
          {!addCard && stripeCardChargeFailed > 0 && (
            <div className="text-sm font-bold text-red-500 flex gap-2 items-center">
              <FaTriangleExclamation />
              You last payment failed, please update your card.
            </div>
          )}
        </div>
      )}

      {!addCard &&
        expiresIn > 31 &&
        !stripeCardChargeFailed &&
        stripeCardId && (
          <div className="text-sm text-emerald-600 flex gap-2 items-center font-bold">
            <FaCircleCheck />
            We will use this card for the next payment.
          </div>
        )}

      {addCard && <CardElement options={{ hidePostalCode: false }} />}
      {!addCard && (
        <PrimaryButton onClick={() => setAddCard(true)} disabled={otherAddCard}>
          {stripeCardId ? "Replace Card" : "Add Card"}
        </PrimaryButton>
      )}
      {addCard && (
        <>
          <div className={"text-xs"}>
            <input
              type="checkbox"
              id={`checkbox-agreement`}
              onChange={(e) => {
                setAgreed(e.target.checked);
              }}
            />
            <label
              htmlFor={`checkbox-agreement`} // Associates label with the checkbox
              className="ml-1"
            >
              I authorize SauceyDirect to securely save my payment card for
              future payments and agree to the terms of service and privacy
              policy.
            </label>
          </div>
          <div className={"flex gap-2"}>
            <SecondaryButton
              width={"full"}
              loading={saving}
              onClick={() => setAddCard(false)}
            >
              Cancel
            </SecondaryButton>
            <PrimaryButton
              width={"full"}
              disabled={!agreed}
              loading={saving}
              onClick={(e) => saveCard(e)}
            >
              Save Card
            </PrimaryButton>
          </div>
        </>
      )}
    </div>
  );
};
function BillingPanel() {
  const { selectedMerchant, selectedLocation } = useSelector(
    (state) => state.app
  );
  const [switchPlanModal, setSwitchPlanModal] = useState(false);
  const [chargeAllModal, setChargeAllModal] = useState(false);
  const [charging, setCharging] = useState(false);
  const [merchant, setMerchant] = useState(null);
  const [fullLocationDetail, setFullLocationDetail] = useState(null);
  const [addCard, setAddCard] = useState(false);
  const [sellerAddCard, setSellerAddCard] = useState(false);
  const [agreed, setAgreed] = useState(false);
  const [sellerAgreed, setSellerAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const dispatch = useDispatch();
  const [pendingCharges, setPendingCharges] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [upcomingInvoice, setUpcomingInvoice] = useState(null);
  const [subscriptionInvoices, setSubscriptionInvoices] = useState([]);
  const [invoiceCount, setInvoiceCount] = useState(0);
  const [invoicePage, setInvoicePage] = useState(1);
  const invoiceLimit = 25;
  const [forceGetInvoice, setForceGetInvoice] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [invoiceStartDate, setInvoiceStartDate] = useState(null);
  const [invoiceEndDate, setInvoiceEndDate] = useState(null);

  const subscriptionStatusColor = useMemo(() => {
    switch (subscription?.status) {
      case "active":
        return "text-emerald-600";
      case "past_due":
        return "text-orange-500";
      case "canceled":
      case "paused":
        return "text-red-500";
      case "trialing":
        return "text-indigo-500";
      default:
        return "text-gray-500";
    }
  }, [subscription]);
  const baseSubscription = useMemo(() => {
    if (!subscription) {
      return null;
    }
    return subscription?.items?.data?.find?.(
      (i) => i.price.product.metadata.type === "direct_base_subscription"
    );
  }, [subscription]);

  const addons = useMemo(() => {
    if (!subscription?.item?.data) {
      return null;
    }
    return subscription.items.data.filter((item) => {
      return item.price.product.metadata.type === "addon";
    });
  }, [subscription]);

  const subscriptionAmountOff = useMemo(() => {
    return (
      (0 +
        subscription?.discounts?.reduce((acc, discount) => {
          return acc + discount.coupon.amount_off;
        }, 0) +
        baseSubscription?.discounts?.reduce((acc, discount) => {
          return acc + discount.coupon.amount_off;
        }, 0)) /
      100
    );
  }, [subscription, baseSubscription]);

  useEffect(() => {
    setMerchant(selectedMerchant);
  }, [selectedMerchant]);

  const _getstore = async () => {
    const res = await getStore();
    setFullLocationDetail(res.store);
  };

  useEffect(() => {
    _getstore();
  }, [selectedLocation]);

  useEffect(() => {
    if (invoicePage === 1) {
      setForceGetInvoice(new Date());
    }
    setInvoicePage(1);
  }, [invoiceStartDate, invoiceEndDate, selectedMerchant]);
  useEffect(() => {
    setForceGetInvoice(new Date());
  }, [invoicePage]);

  useEffect(() => {
    if (!selectedMerchant) return;
    getPendingOrderCharges().then((data) => {
      setPendingCharges(data.orders || []);
    });
    if (invoiceStartDate && invoiceEndDate) {
      getInvoices(
        invoicePage,
        invoiceLimit,
        invoiceStartDate,
        invoiceEndDate
      ).then((data) => {
        setInvoices(data.invoices || []);
        setInvoiceCount(data.count);
      });
    }
    getSubscription()
      .then((data) => {
        setSubscription(data?.subscription || {});
        if (data?.upcomingInvoice) {
          let upcomingInvoice = data.upcoming_invoice;
          upcomingInvoice.processed_at = new Date(
            upcomingInvoice.created * 1000
          );
          upcomingInvoice._id = upcomingInvoice.number;
          upcomingInvoice.amount = upcomingInvoice.total / 100;
          if (upcomingInvoice.status === "draft") {
            upcomingInvoice.status = "upcoming";
          }
          setUpcomingInvoice(upcomingInvoice);
        } else {
          setUpcomingInvoice(null);
        }

        let invoices = data.invoices || [];
        invoices = invoices.map((invoice) => {
          invoice.processed_at = new Date(invoice.created * 1000);
          invoice._id = invoice.number;
          invoice.amount = invoice.total / 100;
          invoice.hosted_invoice_pdf = invoice.invoice_pdf;
          return invoice;
        });

        setSubscriptionInvoices(invoices);
      })
      .catch((e) => {
        setSubscription({});
        setUpcomingInvoice(null);
      });
  }, [invoicePage, selectedMerchant, forceGetInvoice]);

  const totalInvoicePages = useMemo(() => {
    return Math.ceil(invoiceCount / invoiceLimit);
  }, [invoiceCount, invoiceLimit]);

  const sellerCardExpiresIn = useMemo(() => {
    if (!fullLocationDetail) {
      return 9999999;
    }
    if (
      !fullLocationDetail.stripe_card_exp_year ||
      !fullLocationDetail.stripe_card_exp_month
    ) {
      return 9999999;
    }
    const now = new Date();
    const exp = new Date(
      parseInt(fullLocationDetail.stripe_card_exp_year),
      parseInt(fullLocationDetail.stripe_card_exp_month)
    );
    const diff = exp - now;
    return diff / (1000 * 60 * 60 * 24);
  }, [fullLocationDetail]);
  const expiresIn = useMemo(() => {
    // returns days until expiration
    if (!merchant) {
      return 9999999;
    }
    if (!merchant.stripe_card_exp_year || !merchant.stripe_card_exp_month) {
      return 9999999;
    }
    const now = new Date();
    const exp = new Date(
      parseInt(merchant.stripe_card_exp_year),
      parseInt(merchant.stripe_card_exp_month)
    );
    const diff = exp - now;
    return diff / (1000 * 60 * 60 * 24);
  }, [merchant]);

  if (!merchant) return null;
  return (
    <div className="grow">
      {/* Panel body */}
      <div className="p-6 space-y-6">
        <div>
          <h2 className="text-2xl text-slate-800 dark:text-slate-100 font-bold mb-4">
            Billing
          </h2>
          <div className="text-sm">
            Manage your payment methods and see your billing history.
          </div>
        </div>

        <>
          <section className={"flex flex-col gap-4"}>
            <CanView permissions="billing">
              <div>
                <h3 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 ">
                  Payment Method{" "}
                  {["nmi","frame"].includes(fullLocationDetail?.payment_gateway) &&
                  fullLocationDetail?.aggregated_transaction_fee_billing
                    ? " for Subscription and Transaction Charges"
                    : " for Subscription"}
                </h3>
                <SubscriptionElement>
                  <CardInput
                    expiresIn={expiresIn}
                    addCard={addCard}
                    setAddCard={setAddCard}
                    otherAddCard={sellerAddCard}
                    setOtherAddCard={setSellerAddCard}
                    stripeCardId={merchant.stripe_card_id}
                    stripeCardBrand={merchant.stripe_card_brand}
                    stripeCardLast4={merchant.stripe_card_last4}
                    stripeCardExpMonth={merchant.stripe_card_exp_month}
                    stripeCardExpYear={merchant.stripe_card_exp_year}
                    stripeCardChargeFailed={merchant.stripe_card_charge_failed}
                    level="merchant"
                    setForceGetInvoice={setForceGetInvoice}
                    setMerchant={setMerchant}
                  />
                </SubscriptionElement>
              </div>
            </CanView>
            {["nmi","frame"].includes(fullLocationDetail?.payment_gateway) &&
              !fullLocationDetail?.aggregated_transaction_fee_billing && (
                <div>
                  <h3 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 ">
                    Payment Method for {fullLocationDetail?.name}'s Transaction
                    Charges
                  </h3>
                  <BillingElement>
                    <CardInput
                      expiresIn={sellerCardExpiresIn}
                      addCard={sellerAddCard}
                      setAddCard={setSellerAddCard}
                      setOtherAddCard={setAddCard}
                      otherAddCard={addCard}
                      stripeCardId={fullLocationDetail?.stripe_card_id}
                      stripeCardBrand={fullLocationDetail?.stripe_card_brand}
                      stripeCardLast4={fullLocationDetail?.stripe_card_last4}
                      stripeCardExpMonth={
                        fullLocationDetail?.stripe_card_exp_month
                      }
                      stripeCardExpYear={fullLocationDetail?.stripe_card_exp_year}
                      stripeCardChargeFailed={
                        fullLocationDetail?.stripe_card_charge_failed
                      }
                      level="seller"
                      setForceGetInvoice={setForceGetInvoice}
                      setMerchant={setMerchant}
                    />
                  </BillingElement>
                </div>
              )}
          </section>
        </>

        <CanView permissions="billing">
          <section>
            <h3 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 ">
              Subscription
            </h3>
            <div
              className={
                "flex flex-col gap-4 w-[410px] border-slate-1200 py-4 border rounded-lg px-4 justify-center"
              }
            >
              {baseSubscription ? (
                <>
                  <div className="font-bold gap-3 items-center">
                    <span>
                      {baseSubscription.plan.nickname?.replace(
                        "Subscription",
                        ""
                      )}
                    </span>
                    <div className={"text-sm flex gap-2 items-center"}>
                      <PrimaryButton
                        size={"sm"}
                        onClick={() => {
                          setSwitchPlanModal(true);
                        }}
                      >
                        Change Plan
                      </PrimaryButton>
                    </div>
                  </div>
                  <div className="text-sm">
                    {formatValue(baseSubscription.price._unit_price)} x{" "}
                    {baseSubscription.quantity} locations ={" "}
                    {subscriptionAmountOff === 0 &&
                      formatValue(baseSubscription.price._amount) + " / month"}
                    {subscriptionAmountOff > 0 && (
                      <>
                        <span className="line-through text-slate-400">
                          {formatValue(baseSubscription.price._amount)}
                        </span>{" "}
                        <span className={"text-[orange] font-bold"}>
                          {formatValue(
                            baseSubscription.price._amount -
                              subscriptionAmountOff
                          )}
                        </span>
                      </>
                    )}
                    {subscription.discounts?.map?.((discount) => {
                      if (discount.end < Date.now() / 1000) {
                        return null;
                      }
                      return (
                        <div key={"discount_" + discount.coupon.id}>
                          <span className="text-[orange]">
                            {" "}
                            {discount.coupon.name}
                          </span>
                        </div>
                      );
                    })}
                    {baseSubscription.discounts?.map?.((discount) => {
                      if (discount.end < Date.now() / 1000) {
                        return null;
                      }
                      return (
                        <div key={"discount_" + discount.coupon.id}>
                          <span className="text-[orange]">
                            {" "}
                            {discount.coupon.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div className={"text-sm font-bold flex gap-2 flex-col  "}>
                    <div
                      className={
                        "flex flex-row gap-1 items-center " +
                        subscriptionStatusColor
                      }
                    >
                      {subscription.status === "active" && (
                        <>
                          <FaCircleCheck />
                          Your subscription is active
                        </>
                      )}
                      {subscription.status === "past_due" && (
                        <>
                          <FaTriangleExclamation />
                          Your subscription is past due
                        </>
                      )}
                      {subscription.status === "canceled" && (
                        <>
                          <FaTriangleExclamation />
                          Your subscription is canceled
                        </>
                      )}
                      {subscription.status === "trialing" && (
                        <>
                          <FaCircleCheck />
                          Your subscription is on trial
                        </>
                      )}
                    </div>
                    <div className="text-sm">
                      Your subscription will renew on{" "}
                      <strong>
                        {moment
                          .unix(subscription?.items?.data[0]?.current_period_end)
                          .format("MMM D, YYYY")}
                      </strong>
                    </div>
                    {(addons || []).length > 0 && (
                      <div>
                        <div className="">Addons</div>
                        <ul>
                          {addons.map((addon) => {
                            return (
                              <li className="font-medium">
                                {addon.price.product.name} -{" "}
                                {formatValue(addon.price.unit_amount / 100)} /
                                month
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className="text-sm font-bold text-[orange] flex gap-2 items-center">
                    <FaTriangleExclamation />
                    You do not have an active subscription
                  </div>
                  <div className="text-sm">
                    {selectedLocation.prospect ? "Your subscription will be activated when your store goes live!" : "Please contact SauceyDirect to activate your subscription"}
                  </div>
                </>
              )}
            </div>
          </section>
        </CanView>

        <CanView permissions="billing">
          <>
            <section>
              <h3 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 ">
                Subscription Invoices
              </h3>
              <InvoiceTable
                invoices={subscriptionInvoices}
                upcomingInvoice={upcomingInvoice}
              />
              <div className="mt-8">
                {/*{invoicePage > 1 && (*/}
                {/*  <PaginationNumeric*/}
                {/*    onSelect={(p) => setInvoicePage(p + 1)}*/}
                {/*    currentPage={invoicePage - 1}*/}
                {/*    totalPages={totalInvoicePages}*/}
                {/*    isFirstPage={invoicePage === 1}*/}
                {/*    isLastPage={invoicePage === totalInvoicePages}*/}
                {/*  />*/}
                {/*)}*/}
              </div>
            </section>
          </>
        </CanView>

        {["nmi","frame"].includes(fullLocationDetail?.payment_gateway) && (
          <>
            <section>
              <h3 className="text-xl flex items-center gap-2 leading-snug text-slate-800 dark:text-slate-100 font-bold mb-4 ">
                Application Fees{" "}
              </h3>

              <section className="mb-3">
                <h3 className="text-lg leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 ">
                  Pending
                  {config.environment === "development" && (
                    <CanView permissions="saucey_admin">
                      {" "}
                      <PrimaryButton
                        size={"sm"}
                        onClick={() => {
                          setChargeAllModal(true);
                        }}
                      >
                        Charge All Now
                      </PrimaryButton>
                    </CanView>
                  )}
                </h3>
                <PendingChargesTable charges={pendingCharges} />
              </section>

              {/*<PendingChargesTable />*/}
              <h3 className="text-lg leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1 flex items-center gap-2">
                Settled{" "}
                <DateSelect
                  onSelect={({ start, end }) => {
                    setInvoiceStartDate(start);
                    setInvoiceEndDate(end);
                  }}
                />
              </h3>
              <InvoiceTable invoices={[...invoices]} />
              {/*<InvoiceTable invoices={[...pendingCharges, ...invoices]}/>*/}
              <div className="mt-8">
                {totalInvoicePages > 1 && (
                  <PaginationNumeric
                    onSelect={(p) => setInvoicePage(p + 1)}
                    currentPage={invoicePage - 1}
                    totalPages={totalInvoicePages}
                    isFirstPage={invoicePage === 1}
                    isLastPage={invoicePage === totalInvoicePages}
                  />
                )}
              </div>
            </section>
          </>
        )}
      </div>
      <ModalBasic
        modalOpen={chargeAllModal}
        setModalOpen={setChargeAllModal}
        title={"Charge All Orders?"}
        width={"md"}
      >
        <div className="text-sm">
          <p>
            Are you sure you want to charge all pending orders now outside of
            normal billing cycle?
          </p>
          <div className="flex gap-2 mt-4 justify-end">
            <SecondaryButton
              loading={charging}
              onClick={() => setChargeAllModal(false)}
            >
              Cancel
            </SecondaryButton>
            <DangerButton
              loading={charging}
              onClick={async () => {
                setCharging(true);
                try {
                  await chargeAllFeesNow(selectedMerchant.id);
                  dispatch(
                    addToast({
                      type: "success",
                      message: "Charging all orders now",
                      timer: 5,
                    })
                  );
                  setForceGetInvoice(new Date());
                  setCharging(false);
                  setChargeAllModal(false);
                  setCharging(false);
                } catch (e) {
                  let message = "An error occurred";
                  try {
                    const serverMessage = e.response?.data?.error;
                    if (serverMessage) {
                      message = serverMessage;
                    }
                  } catch {}
                  message += ", charges are not captured.";
                  dispatch(
                    addToast({
                      type: "error",
                      message: message,
                      timer: 5,
                    })
                  );
                  setCharging(false);
                }
              }}
            >
              Proceed
            </DangerButton>
          </div>
        </div>
      </ModalBasic>
      <PlanModal isOpen={switchPlanModal} setIsOpen={setSwitchPlanModal} />
    </div>
  );
}

const InvoiceTable = ({ upcomingInvoice, invoices }) => {
  const [viewInvoiceID, setViewInvoiceID] = useState(null);
  if (!invoices && !upcomingInvoice) {
    return null;
  }
  return (
    <div className="flex flex-col gap-6 text-sm">
      <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
        {/* Table header */}
        <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
          <tr>
            {/*<th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">*/}
            {/*  Order Date*/}
            {/*</th>*/}
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Fee Settlement Date
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Invoice Number
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Amount
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Status
            </th>
            <th></th>
          </tr>
        </thead>
        {!invoices.length && !upcomingInvoice && (
          <tbody>
            <tr>
              <td colSpan={4} className="text-center py-3">
                No invoices
              </td>
            </tr>
          </tbody>
        )}
        {[upcomingInvoice, ...invoices].map((invoice) => {
          if (!invoice) return null;
          return (
            <tbody key={invoice._id}>
              <tr>
                {/*<td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">*/}
                {/*  {invoice.processed_at && moment(invoice.processed_at).format("MMM D, YYYY")}*/}
                {/*</td>*/}
                <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  {invoice.processed_at &&
                    moment(invoice.processed_at).format("MMM D, YYYY")}
                </td>
                <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  {invoice.processed_at &&
                    invoice._id?.substring?.(invoice._id.length - 8)}
                </td>
                <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                  {formatValue(invoice.amount)}
                </td>
                <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap flex gap-1 items-center capitalize">
                  {invoice.status}
                  {invoice.status === "FAILED" && (
                    <FaTriangleExclamation className="text-red-500" />
                  )}
                </td>
                <td>
                  {!invoice.object && (
                    <SecondaryButton
                      naked
                      onClick={() => {
                        setViewInvoiceID(invoice._id);
                      }}
                    >
                      <span className={"text-xl"}>
                        {" "}
                        <FaRegEye />
                      </span>
                    </SecondaryButton>
                  )}
                  {invoice.hosted_invoice_pdf && (
                    <SecondaryButton
                      naked
                      onClick={() => {
                        window.open(invoice.hosted_invoice_pdf, "_blank");
                      }}
                    >
                      <span className={"text-xl"}>
                        <FaRegFilePdf />
                      </span>
                    </SecondaryButton>
                  )}
                </td>
              </tr>
            </tbody>
          );
        })}
      </table>
      <InvoiceModal invoiceID={viewInvoiceID} setInvoiceID={setViewInvoiceID} />
    </div>
  );
};
const PendingChargesTable = ({ charges }) => {
  const [viewOrderID, setViewOrderID] = useState(null);
  if (!charges) {
    return null;
  }

  return (
    <div className="flex flex-col gap-6 text-sm">
      <table className="table-auto w-full dark:text-slate-300 divide-y divide-slate-200 dark:divide-slate-700">
        {/* Table header */}
        <thead className="text-xs uppercase text-slate-500 dark:text-slate-400 bg-slate-50 dark:bg-slate-900/20 border-t border-slate-200 dark:border-slate-700">
          <tr>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Order Date
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Order ID
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Amount
            </th>
            <th className="text-left first:pl-5 last:pr-5 py-3 whitespace-nowrap">
              Fee Settlement Date
            </th>
          </tr>
        </thead>
        {!charges.length && (
          <tbody>
            <tr>
              <td colSpan={4} className="text-center py-3">
                No pending charges
              </td>
            </tr>
          </tbody>
        )}

        {charges.map((charge) => (
          <tbody key={charge._id}>
            <tr>
              <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                {moment(charge.order.order_date).format("MMM D, YYYY")}
              </td>
              <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                {charge.order.order_number}
                <SecondaryButton
                  naked
                  onClick={() => {
                    setViewOrderID(charge.order._id);
                  }}
                >
                  <FaRegEye />
                </SecondaryButton>
              </td>
              <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                {formatValue(charge.amount)}
              </td>
              <td className="first:pl-5 last:pr-5 py-3 whitespace-nowrap">
                {moment(charge.process_after).format("MMM D, YYYY")}
              </td>
            </tr>
          </tbody>
        ))}
      </table>
      <PastOrderModal
        setModalOpen={setViewOrderID}
        modalOpen={Boolean(viewOrderID)}
        orderID={viewOrderID}
      />
    </div>
  );
};

export default BillingPanel;
